import React, { useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { PlusIcon } from '@heroicons/react/24/outline';
import { ImportLineItem, HighlightState } from '../types';
import { formatPrix, computeTotalHT } from '../utils/formatters';
import SortableLineItemRow from './SortableLineItemRow';

interface LineItemsTableProps {
  lines: ImportLineItem[];
  highlight: HighlightState;
  modifiedFields: Set<string>;
  onUpdate: (id: string, field: keyof ImportLineItem, value: string | number | null) => void;
  onDelete: (id: string) => void;
  onMerge: (id: string) => void;
  onSplit: (id: string) => void;
  onAdd: () => void;
  onReorder: (activeId: string, overId: string) => void;
  onHover: (id: string | null) => void;
  onClick: (id: string | null) => void;
  registerRef: (id: string, el: HTMLTableRowElement | null) => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string, shiftKey: boolean) => void;
  onToggleSelectAll: () => void;
  extraHeaders?: React.ReactNode;
  renderExtraCells?: (line: ImportLineItem) => React.ReactNode;
  extraColumnCount?: number;
  hiddenColumns?: Set<string>;
}

// All possible data columns (between checkbox and extra/actions)
const DATA_COLUMNS = ['article', 'designation', 'unite', 'quantite', 'prixUnitaire', 'totalHT'];

const LineItemsTable: React.FC<LineItemsTableProps> = ({
  lines,
  highlight,
  modifiedFields,
  onUpdate,
  onDelete,
  onMerge,
  onSplit,
  onAdd,
  onReorder,
  onHover,
  onClick,
  registerRef,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  extraHeaders,
  renderExtraCells,
  extraColumnCount = 0,
  hiddenColumns,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onReorder(String(active.id), String(over.id));
    }
  };

  const totalHT = useMemo(() => {
    return lines.reduce((sum, l) => {
      const t = computeTotalHT(l.quantite, l.prixUnitaire);
      return sum + (t ?? 0);
    }, 0);
  }, [lines]);

  const lineIds = useMemo(() => lines.map((l) => l.id), [lines]);

  const hidden = hiddenColumns ?? new Set<string>();
  const show = (col: string) => !hidden.has(col);
  const visibleDataCount = DATA_COLUMNS.filter((c) => show(c)).length;
  // drag + checkbox + visible data cols + extra cols + actions
  const totalColSpan = 2 + visibleDataCount + extraColumnCount + 1;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
              <th className="w-8 py-2"></th>
              <th className="w-8 py-2 text-center">
                <input
                  type="checkbox"
                  checked={lines.length > 0 && selectedIds.size === lines.length}
                  ref={(el) => {
                    if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < lines.length;
                  }}
                  onChange={onToggleSelectAll}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
                />
              </th>
              {show('article') && <th className="w-24 py-2 text-left px-1">Article</th>}
              {show('designation') && <th className="py-2 text-left px-1">Désignation</th>}
              {show('unite') && <th className="w-16 py-2 text-center px-1">Unité</th>}
              {show('quantite') && <th className="w-20 py-2 text-right px-1">Qté</th>}
              {show('prixUnitaire') && <th className="w-24 py-2 text-right px-1">PU HT</th>}
              {show('totalHT') && <th className="w-28 py-2 text-right px-2">Total HT</th>}
              {extraHeaders}
              <th className="w-10 py-2"></th>
            </tr>
          </thead>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={lineIds} strategy={verticalListSortingStrategy}>
              {lines.map((line, idx) => (
                <SortableLineItemRow
                  key={line.id}
                  line={line}
                  isLast={idx === lines.length - 1}
                  isHovered={highlight.hoveredLineId === line.id}
                  isActive={highlight.activeLineId === line.id}
                  modifiedFields={modifiedFields}
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                  onMerge={onMerge}
                  onSplit={onSplit}
                  onHover={onHover}
                  onClick={onClick}
                  registerRef={registerRef}
                  isSelected={selectedIds.has(line.id)}
                  onToggleSelect={onToggleSelect}
                  renderExtraCells={renderExtraCells}
                  extraColumnCount={extraColumnCount}
                  hiddenColumns={hidden}
                />
              ))}
            </SortableContext>
          </DndContext>
          <tbody>
            <tr className="group cursor-pointer" onClick={onAdd}>
              <td className="w-8 py-1 text-center">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-gray-300 group-hover:bg-violet-100 group-hover:text-violet-600 transition-colors">
                  <PlusIcon className="w-3.5 h-3.5" />
                </span>
              </td>
              <td colSpan={totalColSpan - 1} className="py-1 text-xs text-gray-300 group-hover:text-violet-500 transition-colors">
                Nouvelle ligne
              </td>
            </tr>
          </tbody>
          {show('totalHT') && (
            <tfoot>
              <tr className="border-t border-gray-300">
                <td colSpan={totalColSpan - 2} className="py-2 px-2 text-right text-sm font-semibold text-gray-600">
                  Total HT
                </td>
                <td className="w-28 py-2 px-2 text-right text-sm font-bold text-gray-900">
                  {formatPrix(totalHT)} €
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default LineItemsTable;
